import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { useLanguage } from '@/hooks/useLanguage';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface SegmentedToggleProps<T extends string> {
  value: T;
  onChange: (next: T) => void;
  options: ReadonlyArray<SegmentedOption<T>>;
  /** Visual size — "sm" for inline/compact, "md" default. */
  size?: 'sm' | 'md';
  /** Stretch to fill parent width. */
  fullWidth?: boolean;
  className?: string;
  ariaLabel?: string;
}

/**
 * Pill-shaped segmented control with smooth sliding indicator.
 * Mobile-optimised: ≥40px tap targets, no hover-only states.
 */
export function SegmentedToggle<T extends string>({
  value,
  onChange,
  options,
  size = 'md',
  fullWidth = false,
  className,
  ariaLabel,
}: SegmentedToggleProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRefs   = useRef<Record<string, HTMLButtonElement | null>>({});
  const [pill, setPill] = useState<{ left: number; width: number } | null>(null);
  const { t } = useLanguage();

  const measure = () => {
    const el = buttonRefs.current[value];
    const container = containerRef.current;
    if (!el || !container) return;
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    setPill({ left: eRect.left - cRect.left, width: eRect.width });
  };

  useLayoutEffect(measure, [value, options.length]);

  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [value]);

  // sm: h-8 (mobile-compact), md: h-10
  const heightCls = size === 'sm' ? 'h-8 text-[12px]' : 'h-10 text-[13px]';
  const padCls    = size === 'sm' ? 'px-2.5' : 'px-3.5';

  return (
    <div
      ref={containerRef}
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'relative inline-flex items-center bg-ink-100/80 rounded-full p-0.5 select-none',
        'segmented-toggle-container',
        fullWidth && 'flex w-full',
        className,
      )}
    >
      {/* Sliding indicator pill */}
      {pill ? (
        <div
          aria-hidden
          className={cn(
            'absolute top-0.5 bottom-0.5 rounded-full bg-white',
            'segmented-toggle-pill',
            'shadow-[0_1px_3px_rgba(15,23,42,0.08),0_1px_1px_rgba(15,23,42,0.04)]',
            'transition-all duration-[280ms] ease-[cubic-bezier(0.16,1,0.3,1)]',
          )}
          style={{ transform: `translateX(${pill.left - 2}px)`, width: pill.width }}
        />
      ) : null}

      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            ref={(el) => { buttonRefs.current[opt.value] = el; }}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={opt.disabled}
            onClick={() => !opt.disabled && onChange(opt.value)}
            className={cn(
              'relative z-10 flex items-center justify-center gap-1 rounded-full font-semibold',
              'transition-colors duration-150 whitespace-nowrap',
              heightCls, padCls,
              fullWidth && 'flex-1',
              active ? 'text-ink-900' : 'text-ink-500',
              opt.disabled && 'opacity-40 cursor-not-allowed',
            )}
          >
            {opt.icon}
            <span>{t(opt.label)}</span>
          </button>
        );
      })}
    </div>
  );
}
